import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppLink } from '../components/SiteShell';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { Checkbox } from '../components/Checkbox';
import { Button } from '../components/Button';
import { firma, start, leistungen, nichtAbschliessend, kontakt } from '../content';
import './Home.css';

// "Datenschutzerklärung" innerhalb des vorgegebenen Hinweistexts verlinken,
// ohne den Wortlaut aus content.ts zu veraendern oder zu verdoppeln.
const [datenschutzHinweisVor, datenschutzHinweisNach] = kontakt.datenschutzHinweis.split(
  'Datenschutzerklärung',
);

export function Home() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);
  /** Honigtopf. Menschen sehen dieses Feld nicht - siehe Markup unten. */
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<'ruhe' | 'sendet' | 'fertig'>('ruhe');
  const [fehler, setFehler] = useState<string | null>(null);

  /**
   * Echter Versand über /kontakt. Der Dienst dahinter speichert nichts - die
   * Nachricht geht in den Posteingang und ist danach vergessen.
   *
   * Der Pfad ist bewusst NICHT /api/: Unter mlaenu.de ist /api/ bereits an
   * OmniRoute vergeben. Zwei Dienste unter demselben Pfad wären ein Fehler,
   * den man erst bemerkt, wenn einer von beiden schweigt.
   */
  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('sendet');
    setFehler(null);
    try {
      const res = await fetch('/kontakt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, website }),
      });
      if (res.status === 204) {
        setStatus('fertig');
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { detail?: string };
      setFehler(
        body.detail ??
          `Die Nachricht konnte nicht gesendet werden. Bitte schreiben Sie uns direkt an ${firma.mailAllgemein}.`,
      );
      setStatus('ruhe');
    } catch {
      // Netz weg oder Dienst aus: den direkten Weg nennen, statt den Besuch
      // mit einem Fehler stehen zu lassen.
      setFehler(
        `Die Verbindung ist fehlgeschlagen. Bitte schreiben Sie uns direkt an ${firma.mailAllgemein}.`,
      );
      setStatus('ruhe');
    }
  }

  return (
    <>
      <section className="home-hero">
        <div className="container home-hero__inner">
          <Badge>{start.auszeichnung}</Badge>
          <h1 className="home-hero__title">{start.titel}</h1>
          <p className="home-hero__lead">{start.vorspann}</p>
        </div>
      </section>

      <section className="home-services" aria-label="Unsere Leistungen">
        <div className="container home-services__grid">
          {leistungen.map((leistung) => (
            <Card key={leistung.titel} title={leistung.titel} text={leistung.text} />
          ))}
        </div>
      </section>

      <section className="home-open">
        <div className="container home-open__inner">
          <h2 className="home-open__title">{nichtAbschliessend.titel}</h2>
          <p className="home-open__text">{nichtAbschliessend.text}</p>
        </div>
      </section>

      <section className="home-contact" aria-labelledby="contact-title">
        <div className="container home-contact__grid">
          <div className="home-contact__intro">
            <h2 id="contact-title" className="home-contact__title">
              {kontakt.titel}
            </h2>
            <p className="home-contact__text">{kontakt.text}</p>
            <p className="home-contact__assurance">{kontakt.zusage}</p>
          </div>

          <form className="home-contact__form" onSubmit={handleSubmit}>
            <Input
              id="contact-name"
              label="Name"
              name="name"
              autoComplete="name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Input
              id="contact-email"
              label="E-Mail"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <label className="home-contact__field">
              <span className="home-contact__label">Ihre Nachricht</span>
              <textarea
                id="contact-message"
                name="message"
                className="home-contact__textarea"
                rows={5}
                required
                minLength={10}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
            </label>
            <Checkbox
              id="contact-consent"
              required
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              label={
                <>
                  {datenschutzHinweisVor}
                  <AppLink to="/datenschutz" className="home-contact__privacy-link">
                    Datenschutzerklärung
                  </AppLink>
                  {datenschutzHinweisNach}
                </>
              }
            />
            {/*
              Honigtopf: für Menschen unsichtbar und aus der Vorlesereihenfolge
              genommen. `display:none` allein reicht nicht - manche
              Ausfüllhilfen springen trotzdem hinein; `tabIndex={-1}` und
              `aria-hidden` halten Tastatur und Screenreader fern.
            */}
            <input
              type="text"
              name="website"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              autoComplete="off"
              className="home-contact__honeypot"
            />
            <Button type="submit" disabled={status === 'sendet'}>
              {status === 'sendet' ? 'Wird gesendet …' : kontakt.absenden}
            </Button>
            {status === 'fertig' && (
              <p className="home-contact__done" role="status">
                Vielen Dank, Ihre Nachricht ist angekommen. {kontakt.zusage}
              </p>
            )}
            {fehler && (
              <p className="home-contact__error" role="alert">
                {fehler}
              </p>
            )}
          </form>
        </div>
      </section>
    </>
  );
}
